import { UlmButton } from "@components/ulm/ulm-button";
import { UlmContainer } from "@components/ulm/ulm-container";
import { UlmPasswordField } from "@components/ulm/ulm-password-field";
import { Box, Stack } from "@mui-wrapper";
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  MIN_PASSWORD_LENGTH,
  PasswordValidationChecker,
  useToast,
} from "@flat-ui";
import { PasswordChangeSuccess } from "@pages/password-change/password-change-success";
import { useSearchParams } from "react-router-dom";
import { QueryParams } from "@router/route-params";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { PageTitle } from "@components/common/page-title";
import { PageSubtitle } from "@components/common/page-subtitle";
import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import { HttpUtils } from "@stellar/utils";
import { getErrorDisplayMarkup } from "@context-providers/error-handler/error-handler-utils";

/**
 * Page that allows users to change their password.
 */
export function PasswordChange(): JSX.Element {
  const [searchParams] = useSearchParams();
  const keyValue = searchParams.get(QueryParams.key) || undefined;
  const { openToast } = useToast();
  const { handleError } = useErrorHandlerContext();

  const [confirmPasswordError, setConfirmPasswordError] = useState<string>();
  const [isPasswordResetting, setIsPasswordResetting] =
    useState<boolean>(false);
  const [isResetPasswordWithSuccess, setIsResetPasswordWithSuccess] =
    useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);

  const isButtonDisabled = useMemo(() => {
    return password !== confirmPassword || !isPasswordValid;
  }, [password, confirmPassword, isPasswordValid]);

  const resetPassword = useCallback(async () => {
    try {
      setIsPasswordResetting(true);

      await faroCoreApiClient.users.setNewPassword({
        /* eslint-disable @typescript-eslint/naming-convention -- naming defined by backend */
        setnewpw_key: keyValue,
        setnewpw_pw: password,
        setnewpw_pw_2: confirmPassword,
        /* eslint-enable @typescript-eslint/naming-convention */
      });

      setIsResetPasswordWithSuccess(true);
    } catch (error) {
      const title = "Failed to reset the password.";
      // The backend request fails with 400 if the key is missing or with 404 if the key is invalid
      // or if the new password is the same as the old password. For those cases we should not log
      // the error to Sentry.
      const errorStatus = HttpUtils.getErrorStatus(error);
      const isInvalidOrMissingKeyError =
        errorStatus !== undefined &&
        (errorStatus === HttpUtils.HTTP_ERROR_CODE.NotFound ||
          errorStatus === HttpUtils.HTTP_ERROR_CODE.BadRequest);
      if (isInvalidOrMissingKeyError) {
        openToast({
          title,
          message: getErrorDisplayMarkup(error),
          variant: "error",
        });
      } else {
        handleError({
          id: `setNewPassword-${Date.now().toString()}`,
          title,
          error,
          uiType: "toast",
        });
      }
    } finally {
      setIsPasswordResetting(false);
    }
  }, [confirmPassword, handleError, keyValue, openToast, password]);

  const onPasswordKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (!isButtonDisabled && event.key === "Enter") {
        void resetPassword();
      }
    },
    [isButtonDisabled, resetPassword]
  );

  function validatePasswords(
    newPassword: string,
    newConfirmPassword: string
  ): void {
    if (newConfirmPassword.length >= newPassword.length) {
      let errorMessage: string | undefined;
      if (newConfirmPassword !== newPassword) {
        errorMessage = "Passwords do not match";
      }
      setConfirmPasswordError(errorMessage);
    } else {
      setConfirmPasswordError(undefined);
    }
  }

  function updateConfirmPassword(event: ChangeEvent<HTMLInputElement>): void {
    const newConfirmPassword = event.target.value;
    setConfirmPassword(newConfirmPassword);
    validatePasswords(password, newConfirmPassword);
  }

  function updatePassword(event: ChangeEvent<HTMLInputElement>): void {
    const newPassword = event.target.value;
    setPassword(newPassword);
    validatePasswords(newPassword, confirmPassword);
  }

  if (isResetPasswordWithSuccess) {
    return <PasswordChangeSuccess />;
  }

  return (
    <UlmContainer>
      <PageTitle>Reset your password?</PageTitle>
      <PageSubtitle>
        {"You want something secure and no shorter than "}
        <var>{MIN_PASSWORD_LENGTH}</var>
        {" characters."}
      </PageSubtitle>

      <Stack
        sx={{
          marginTop: "100px",
          width: "100%",
        }}
      >
        <UlmPasswordField
          value={password}
          placeholder={"Enter your new password"}
          hasError={!isPasswordValid && !!password.length}
          onKeyUp={onPasswordKeyUp}
          onChange={updatePassword}
        />

        <Box
          sx={{
            marginBottom: "12px",
          }}
        >
          <PasswordValidationChecker
            password={password}
            onValidationChange={setIsPasswordValid}
          />
        </Box>

        <UlmPasswordField
          value={confirmPassword}
          placeholder={"Confirm your new password"}
          label={"Confirm password"}
          helperText={confirmPasswordError}
          hasError={!!confirmPasswordError}
          onKeyUp={onPasswordKeyUp}
          onChange={updateConfirmPassword}
        />
      </Stack>

      <UlmButton
        disabled={isButtonDisabled}
        onClick={resetPassword}
        isLoading={isPasswordResetting}
        isLoadingText="Updating..."
        sx={{
          marginTop: "8px",
        }}
      >
        Set password
      </UlmButton>
    </UlmContainer>
  );
}
